import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  ModalBody,
  ModalContent,
  ModalFooter,
} from "@chakra-ui/react";
import hj from "@hotjar/browser";
import {
  type ChainFullId,
  ChainFullIds,
  type RawPkh,
  type TokenTransfer,
  type UnifiedTokenBalance,
  formatTokenAmount,
  getRawAmount,
  isTezosToken,
  parseChainAddress,
  parseTokenContractAddress,
  tokenDecimals,
  tokenSymbolSafe,
} from "@umami/chains";
import { FormProvider, useForm } from "react-hook-form";

import { SignPage } from "./SignPage";
import { KnownAccountsAutocomplete } from "../../AddressAutocomplete";
import { TokenTile } from "../../AssetTiles";
import { FormPageHeader } from "../FormPageHeader";
import {
  useAddToBatchFormAction,
  useHandleOnSubmitFormActions,
  useOpenSignPageFormAction,
} from "../onSubmitFormActionHooks";
import {
  type FormPagePropsWithSender,
  FormSubmitButton,
  formDefaultValues,
  getSmallestUnit,
  makeValidateDecimals,
} from "../utils";

export type FormValues = {
  sender: RawPkh;
  recipient: RawPkh;
  prettyAmount: string;
  chainId: ChainFullId;
};

const getTokenDetails = (token: UnifiedTokenBalance) => {
  if (isTezosToken(token)) {
    const decimals = tokenDecimals(token);
    const prettyBalance = formatTokenAmount(token.balance, decimals);
    const smallestUnit = getSmallestUnit(Number(decimals));

    return { decimals, prettyBalance, smallestUnit };
  }

  return {
    decimals: token.decimals,
    prettyBalance: token.balance,
    smallestUnit: getSmallestUnit(token.decimals),
  };
};

const getTokenSymbol = (token: UnifiedTokenBalance) =>
  isTezosToken(token) ? tokenSymbolSafe(token) : token.symbol;

export const FormPage = (
  props: FormPagePropsWithSender<FormValues> & { token: UnifiedTokenBalance }
) => {
  const { token } = props;
  const chainId = props.chainId ?? ChainFullIds.TEZOS;

  const openSignPage = useOpenSignPageFormAction({
    SignPage,
    signPageExtraData: { token },
    FormPage,
    defaultFormPageProps: props,
    toOperation: toOperation(token),
    chainId,
  });

  const addToBatch = useAddToBatchFormAction(toOperation(token));

  hj.stateChange("send_flow/token_form_page");

  const {
    onFormSubmitActionHandlers: [onSingleSubmit],
    isLoading,
  } = useHandleOnSubmitFormActions([openSignPage, addToBatch]);

  const form = useForm<FormValues>({
    mode: "onBlur",
    defaultValues: { ...formDefaultValues(props), chainId },
  });

  const {
    formState: { errors },
    register,
    handleSubmit,
  } = form;

  const { decimals, prettyBalance, smallestUnit } = getTokenDetails(token);

  return (
    <FormProvider {...form}>
      <ModalContent>
        <form onSubmit={handleSubmit(onSingleSubmit)}>
          <FormPageHeader />
          <ModalBody gap="24px">
            <FormControl data-testid="available-balance">
              <FormLabel>Available balance</FormLabel>
              <TokenTile token={token} />
            </FormControl>
            <FormControl isInvalid={!!errors.prettyAmount}>
              <FormLabel>Amount</FormLabel>
              <InputGroup>
                <Input
                  isDisabled={isLoading}
                  step={smallestUnit}
                  type="number"
                  {...register("prettyAmount", {
                    required: "Amount is required",
                    max: {
                      value: prettyBalance.toString(),
                      message: `Max amount is ${prettyBalance}`,
                    },
                    validate: makeValidateDecimals(Number(decimals)),
                  })}
                  placeholder={smallestUnit}
                />
                <InputRightElement paddingRight="12px" data-testid="token-symbol">
                  {getTokenSymbol(token)}
                </InputRightElement>
              </InputGroup>
              {errors.prettyAmount && (
                <FormErrorMessage data-testid="amount-error">
                  {errors.prettyAmount.message}
                </FormErrorMessage>
              )}
            </FormControl>
            <FormControl isInvalid={!!errors.recipient}>
              <KnownAccountsAutocomplete allowUnknown inputName="recipient" label="To" />
              {errors.recipient && (
                <FormErrorMessage data-testid="recipient-error">
                  {errors.recipient.message}
                </FormErrorMessage>
              )}
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <FormSubmitButton isLoading={isLoading} />
          </ModalFooter>
        </form>
      </ModalContent>
    </FormProvider>
  );
};

const toOperation =
  (token: UnifiedTokenBalance) =>
  (formValues: FormValues): TokenTransfer => {
    const operation: TokenTransfer = {
      type: "fa2",
      sender: parseChainAddress(formValues.sender, formValues.chainId),
      recipient: parseChainAddress(formValues.recipient, formValues.chainId),
      contract: parseTokenContractAddress(token),
      tokenId: isTezosToken(token) ? token.tokenId : "0",
      amount: getRawAmount(token, formValues.prettyAmount),
    };

    if (token.type === "fa2") {
      return operation;
    }

    return { ...operation, type: "fa1.2", tokenId: "0" };
  };
